import { useState, useMemo, useCallback } from 'react';
import {
  LogIn,
  LogOut,
  BedDouble,
  Users,
  Home,
  Loader2,
  CheckCircle2,
  Circle,
  X,
  Rocket,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  CalendarDays,
  Hotel,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, isToday, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { BookingStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useBookings, useCheckInBooking, useCheckOutBooking } from '@/hooks/api/useBookings';
import { useProperties } from '@/hooks/api/useProperties';
import { SlideOver } from '@/components/ui/SlideOver';
import { PrintBillButton } from '@/components/ui/PrintBillButton';
import { useOnboardingStatus } from '@/hooks/api/useOnboarding';
import { useDashboardKpis } from '@/hooks/api/useDashboard';
import { useSelectedPropertyId, useIsPortfolio } from '@/contexts/PropertyContext';
import { PortfolioDashboard } from '@/components/dashboard/PortfolioDashboard';
import {
  useArrivalsMultiDay,
  useDeparturesMultiDay,
  type DayGroup,
} from '@/hooks/api/useFrontDesk';
import type { BookingEntry } from '@/services/api/frontdesk.api';

// ─── Types ─────────────────────────────────────────────────────────────────────

type HousekeepingStatus = 'clean' | 'dirty' | 'inspected' | 'occupied' | 'maintenance' | 'out_of_order';

interface UnitData {
  id: string;
  propertyId: string;
  propertyName: string;
  name: string;
  type: string;
  capacity: number;
  basePrice: string;
  housekeepingStatus: HousekeepingStatus;
}

interface BookingData {
  id: string;
  unitId: string;
  guestId: string | null;
  guestName?: string;
  guest?: { id: string; fullName: string; email?: string | null } | null;
  status: BookingStatus;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  totalAmount: string;
  currency: string;
  source: string;
  specialRequests?: string | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d');
  } catch {
    return iso;
  }
}

function nightsCount(checkIn: string, checkOut: string): number {
  try {
    const ci = parseISO(checkIn);
    const co = parseISO(checkOut);
    return Math.max(1, Math.round((co.getTime() - ci.getTime()) / 86400000));
  } catch {
    return 1;
  }
}

function isTodayDate(iso: string): boolean {
  try {
    return isToday(parseISO(iso));
  } catch {
    return false;
  }
}

// ─── Housekeeping status config ─────────────────────────────────────────────

const HK_CONFIG: Record<HousekeepingStatus, { label: string; dot: string; cardBg: string; cardBorder: string; badge: string }> = {
  clean: {
    label: 'Clean',
    dot: 'bg-green-500',
    cardBg: 'bg-white',
    cardBorder: 'border-l-green-500',
    badge: 'bg-green-100 text-green-800',
  },
  inspected: {
    label: 'Inspected',
    dot: 'bg-green-400',
    cardBg: 'bg-white',
    cardBorder: 'border-l-green-400',
    badge: 'bg-green-100 text-green-800',
  },
  dirty: {
    label: 'Dirty',
    dot: 'bg-red-500',
    cardBg: 'bg-red-50',
    cardBorder: 'border-l-red-500',
    badge: 'bg-red-100 text-red-800',
  },
  occupied: {
    label: 'Occupied',
    dot: 'bg-blue-500',
    cardBg: 'bg-blue-50',
    cardBorder: 'border-l-blue-500',
    badge: 'bg-blue-100 text-blue-800',
  },
  maintenance: {
    label: 'Maintenance',
    dot: 'bg-gray-500',
    cardBg: 'bg-gray-50',
    cardBorder: 'border-l-gray-400',
    badge: 'bg-gray-100 text-gray-800',
  },
  out_of_order: {
    label: 'Out of Order',
    dot: 'bg-gray-600',
    cardBg: 'bg-gray-50',
    cardBorder: 'border-l-gray-500',
    badge: 'bg-gray-100 text-gray-800',
  },
};

const SOURCE_BADGE_COLORS: Record<string, string> = {
  airbnb: 'bg-rose-100 text-rose-700',
  booking_com: 'bg-blue-100 text-blue-700',
  expedia: 'bg-yellow-100 text-yellow-700',
  direct: 'bg-emerald-100 text-emerald-700',
  manual: 'bg-gray-100 text-gray-600',
};

const SOURCE_LABELS: Record<string, string> = {
  airbnb: 'Airbnb',
  booking_com: 'Booking.com',
  expedia: 'Expedia',
  direct: 'Direct',
  manual: 'Manual',
};

// ─── KPI Strip ─────────────────────────────────────────────────────────────────

interface KpiStripItem {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}

function KpiStrip({ items }: { items: KpiStripItem[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="bg-white rounded-lg border border-gray-200 p-5"
          aria-label={`${item.label}: ${item.value}`}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-500">{item.label}</p>
            <div className={cn('p-2 rounded-lg shrink-0', item.color)}>
              {item.icon}
            </div>
          </div>
          <p className="text-2xl font-semibold text-gray-900 mt-2">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Unit Card ─────────────────────────────────────────────────────────────────

interface UnitCardProps {
  unit: UnitData;
  currentBooking: BookingData | undefined;
  arrivingToday: boolean;
  departingToday: boolean;
  onClick: () => void;
}

function UnitCard({ unit, currentBooking, arrivingToday, departingToday, onClick }: UnitCardProps) {
  const hkStatus = (unit.housekeepingStatus as HousekeepingStatus) || 'clean';

  // Determine visual style based on priority: departing > arriving > occupied > hk status
  let cardBg = HK_CONFIG[hkStatus]?.cardBg ?? 'bg-white';
  let cardBorder = HK_CONFIG[hkStatus]?.cardBorder ?? 'border-l-gray-200';
  if (departingToday) {
    cardBg = 'bg-orange-50';
    cardBorder = 'border-l-orange-400';
  } else if (arrivingToday) {
    cardBg = 'bg-green-50';
    cardBorder = 'border-l-green-400';
  } else if (currentBooking?.status === BookingStatus.CHECKED_IN) {
    cardBg = 'bg-blue-50';
    cardBorder = 'border-l-blue-500';
  }

  const hkConfig = HK_CONFIG[hkStatus] ?? HK_CONFIG.clean;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xl border border-gray-200 border-l-4 p-3 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 w-full',
        cardBg,
        cardBorder,
      )}
      aria-label={`Unit ${unit.name}${currentBooking ? `, ${currentBooking.guest?.fullName ?? currentBooking.guestName ?? 'Guest'}` : ''}`}
    >
      <div className="flex items-start justify-between gap-1 mb-1.5">
        <span className="font-semibold text-gray-900 text-sm truncate">{unit.name}</span>
        <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0', hkConfig.badge)}>
          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', hkConfig.dot)} aria-hidden="true" />
          {hkConfig.label}
        </span>
      </div>

      <p className="text-xs text-gray-400 truncate mb-1.5">{unit.propertyName}</p>

      {currentBooking && (
        <p className="text-xs text-gray-600 truncate">
          {currentBooking.guest?.fullName ?? currentBooking.guestName ?? 'Guest'} · out {fmtDate(currentBooking.checkOut)}
        </p>
      )}

      <div className="flex gap-1 mt-1.5 flex-wrap">
        {arrivingToday && (
          <span className="inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">
            ARRIVAL
          </span>
        )}
        {departingToday && (
          <span className="inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
            DEPARTURE
          </span>
        )}
      </div>
    </button>
  );
}

// ─── Booking Detail Slide-over ────────────────────────────────────────────────

interface BookingDetailProps {
  booking: BookingData | null;
  unit: UnitData | null;
  onClose: () => void;
  onCheckIn: (id: string) => void;
  onCheckOut: (id: string) => void;
  isCheckingIn: boolean;
  isCheckingOut: boolean;
}

function BookingDetailSlideOver({
  booking,
  unit,
  onClose,
  onCheckIn,
  onCheckOut,
  isCheckingIn,
  isCheckingOut,
}: BookingDetailProps) {
  const canCheckIn = booking?.status === BookingStatus.CONFIRMED;
  const canCheckOut = booking?.status === BookingStatus.CHECKED_IN;

  return (
    <SlideOver isOpen={!!booking} onClose={onClose} title={unit?.name ?? 'Unit Details'}>
      {booking && unit && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Status</span>
              <StatusBadge status={booking.status} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Guest</span>
              <span className="text-sm font-semibold text-gray-900">{booking.guest?.fullName ?? booking.guestName ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Check-in</span>
              <span className="text-sm text-gray-700">{fmtDate(booking.checkIn)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Check-out</span>
              <span className="text-sm text-gray-700">{fmtDate(booking.checkOut)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Nights</span>
              <span className="text-sm text-gray-700">{nightsCount(booking.checkIn, booking.checkOut)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Guests</span>
              <span className="text-sm text-gray-700">{booking.guestsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Total</span>
              <span className="text-sm font-semibold text-gray-900">
                {booking.currency ?? 'USD'} {(parseFloat(booking.totalAmount) || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Source</span>
              <span className={cn('inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full', SOURCE_BADGE_COLORS[booking.source] ?? 'bg-gray-100 text-gray-600')}>
                {SOURCE_LABELS[booking.source] ?? booking.source}
              </span>
            </div>
          </div>

          {booking.specialRequests && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Special Requests</h3>
              <p className="text-sm text-gray-600 bg-yellow-50 rounded-lg p-3">{booking.specialRequests}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {canCheckIn && (
              <button
                type="button"
                onClick={() => onCheckIn(booking.id)}
                disabled={isCheckingIn}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
              >
                {isCheckingIn ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                Check In Guest
              </button>
            )}
            {canCheckOut && (
              <button
                type="button"
                onClick={() => onCheckOut(booking.id)}
                disabled={isCheckingOut}
                className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
              >
                {isCheckingOut ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                Check Out Guest
              </button>
            )}
          </div>
        </div>
      )}
    </SlideOver>
  );
}

// ─── Status Badge helper ───────────────────────────────────────────────────────

const STATUS_STYLES: Record<BookingStatus, string> = {
  [BookingStatus.CONFIRMED]: 'bg-green-100 text-green-800',
  [BookingStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [BookingStatus.CHECKED_IN]: 'bg-blue-100 text-blue-800',
  [BookingStatus.CHECKED_OUT]: 'bg-slate-100 text-slate-600',
  [BookingStatus.CANCELLED]: 'bg-red-100 text-red-800',
  [BookingStatus.NO_SHOW]: 'bg-orange-100 text-orange-800',
};

const STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.CONFIRMED]: 'Confirmed',
  [BookingStatus.PENDING]: 'Pending',
  [BookingStatus.CHECKED_IN]: 'Checked In',
  [BookingStatus.CHECKED_OUT]: 'Checked Out',
  [BookingStatus.CANCELLED]: 'Cancelled',
  [BookingStatus.NO_SHOW]: 'No Show',
};

function StatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600')}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── 7-Day Queue ────────────────────────────────────────────────────────────────

function formatDayHeader(dateStr: string): string {
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return `Today, ${format(d, 'MMM d')}`;
    return format(d, 'EEE, MMM d');
  } catch {
    return dateStr;
  }
}

interface SevenDayQueueProps {
  title: string;
  icon: React.ReactNode;
  dayGroups: DayGroup[];
  isLoading: boolean;
  emptyDayMessage: string;
  renderEntry: (entry: BookingEntry) => React.ReactNode;
  actionVariantColor: string;
}

function SevenDayQueue({
  title,
  icon,
  dayGroups,
  isLoading,
  emptyDayMessage,
  renderEntry,
}: SevenDayQueueProps) {
  const totalCount = dayGroups.reduce((sum, g) => sum + g.entries.length, 0);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>(() => {
    // Expand the first two days by default
    const initial: Record<string, boolean> = {};
    dayGroups.slice(0, 2).forEach((g) => { initial[g.date] = true; });
    return initial;
  });

  function toggleDay(date: string) {
    setExpandedDays((prev) => ({ ...prev, [date]: !prev[date] }));
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <span className="text-gray-400" aria-hidden="true">{icon}</span>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <span className="ml-auto text-sm font-medium text-gray-500">{totalCount} total</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {dayGroups.map((group) => {
            const isExpanded = expandedDays[group.date] ?? false;
            const count = group.entries.length;
            return (
              <div key={group.date}>
                <button
                  type="button"
                  onClick={() => toggleDay(group.date)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors text-left"
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-gray-400 shrink-0" aria-hidden="true" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-400 shrink-0" aria-hidden="true" />
                    )}
                    <span className="text-sm font-semibold text-gray-800">
                      {formatDayHeader(group.date)}
                    </span>
                  </div>
                  <span className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                    count > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500',
                  )}>
                    {count} {count === 1 ? 'booking' : 'bookings'}
                  </span>
                </button>

                {isExpanded && (
                  <div className="px-5 pb-3">
                    {count === 0 ? (
                      <p className="text-sm text-gray-400 pl-7 py-2">{emptyDayMessage}</p>
                    ) : (
                      <ul className="space-y-2 pl-7" role="list">
                        {group.entries.map((entry) => (
                          <li key={entry.id}>
                            {renderEntry(entry)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Checklist config ──────────────────────────────────────────────────────────

const CHECKLIST_STORAGE_KEY = 'pms_checklist_dismissed';

interface ChecklistItem {
  key: string;
  label: string;
  link: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  { key: 'propertyCreated', label: 'Create your first property', link: '/properties' },
  { key: 'unitsCreated', label: 'Add units/rooms', link: '/properties' },
  { key: 'pricingConfigured', label: 'Configure pricing', link: '/pricing' },
  { key: 'policiesConfigured', label: 'Set policies', link: '/property-settings' },
  { key: 'photosUploaded', label: 'Upload photos', link: '/properties' },
  { key: 'teamInvited', label: 'Invite team members', link: '/users' },
  { key: 'channelsConnected', label: 'Connect channels', link: '/channel-manager' },
  { key: 'testBookingCreated', label: 'Create a test booking', link: '/reservations' },
  { key: 'paymentSetup', label: 'Set up payments', link: '/property-settings' },
];

function GettingStartedChecklist() {
  const { data: status } = useOnboardingStatus();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHECKLIST_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(CHECKLIST_STORAGE_KEY, 'true');
    } catch {
      // ignore storage errors
    }
  }, []);

  if (dismissed || !status) return null;

  // Onboarding finished (completed OR skipped — both set `completed: true`).
  // Once done, the backend clears the checklist to `{}`, which would otherwise
  // render a misleading "0 of 9 complete / 0%" on the manager's primary screen.
  if (status.completed) return null;

  const checklist = status.checklist;
  const completedCount = CHECKLIST_ITEMS.filter(
    (item) => checklist[item.key] === true,
  ).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const allDone = completedCount === totalCount;

  if (allDone) return null;

  const pct = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Rocket size={18} className="text-primary-600" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-900">Getting Started</h2>
          <span className="ml-2 text-xs font-medium text-gray-500">
            {completedCount} of {totalCount} complete
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          title="Dismiss checklist"
          aria-label="Dismiss getting started checklist"
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="px-5 py-4">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-600">{pct}% complete</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Items */}
        <ul className="space-y-1" role="list">
          {CHECKLIST_ITEMS.map((item) => {
            const done = checklist[item.key] === true;
            return (
              <li key={item.key}>
                {done ? (
                  <div className="flex items-center gap-3 px-2 py-1.5 rounded-lg">
                    <CheckCircle2
                      size={18}
                      className="text-green-500 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-400 line-through">
                      {item.label}
                    </span>
                  </div>
                ) : (
                  <Link
                    to={item.link}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <Circle
                      size={18}
                      className="text-gray-300 group-hover:text-primary-400 shrink-0 transition-colors"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-primary-600 transition-colors">
                      {item.label}
                    </span>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ─── Revenue KPI Card ──────────────────────────────────────────────────────────

interface RevenueKpiCardProps {
  label: string;
  value: string;
  trend: number | undefined;
  icon: React.ReactNode;
  iconBg: string;
}

function RevenueKpiCard({ label, value, trend, icon, iconBg }: RevenueKpiCardProps) {
  const hasTrend = trend !== undefined && trend !== 0;
  const isPositive = (trend ?? 0) >= 0;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5"
      aria-label={`${label}: ${value}${hasTrend ? `, trend ${isPositive ? 'up' : 'down'} ${Math.abs(trend ?? 0).toFixed(1)}%` : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className={cn('p-2 rounded-lg shrink-0', iconBg)} aria-hidden="true">
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {hasTrend && (
        <div className="flex items-center gap-1 mt-1">
          {isPositive ? (
            <TrendingUp size={14} className="text-green-500" aria-hidden="true" />
          ) : (
            <TrendingDown size={14} className="text-red-500" aria-hidden="true" />
          )}
          <span className={cn('text-sm font-medium', isPositive ? 'text-green-600' : 'text-red-600')}>
            {isPositive ? '+' : ''}{(trend ?? 0).toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Today Snapshot Card ──────────────────────────────────────────────────────

interface SnapshotCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  iconBg: string;
}

function SnapshotCard({ label, value, icon, iconBg }: SnapshotCardProps) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
      aria-label={`${label}: ${value}`}
    >
      <div className={cn('p-2.5 rounded-lg shrink-0', iconBg)} aria-hidden="true">
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}

// ─── Date Range Selector ──────────────────────────────────────────────────────

interface DateRangeSelectorProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

function DateRangeSelector({ from, to, onChange }: DateRangeSelectorProps) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="kpi-from" className="sr-only">From date</label>
      <input
        id="kpi-from"
        type="date"
        value={from}
        onChange={(e) => onChange(e.target.value, to)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        aria-label="KPI start date"
      />
      <span className="text-gray-400 text-sm">to</span>
      <label htmlFor="kpi-to" className="sr-only">To date</label>
      <input
        id="kpi-to"
        type="date"
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        aria-label="KPI end date"
      />
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────

/**
 * Dashboard entry point. In portfolio mode ("All properties") it renders the
 * cross-property rollup; otherwise the single-property operational dashboard.
 * Branching at this altitude keeps each variant's hooks isolated (React hook
 * rules) and leaves the single-property experience byte-for-byte unchanged.
 */
export function DashboardPage() {
  const isPortfolio = useIsPortfolio();
  if (isPortfolio) {
    return <PortfolioDashboard />;
  }
  return <SinglePropertyDashboard />;
}

function SinglePropertyDashboard() {
  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitData | null>(null);

  // Date range for KPIs — default to current month
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => {
    const now = new Date();
    return {
      from: format(startOfMonth(now), 'yyyy-MM-dd'),
      to: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  });

  // Property context for KPIs
  const propertyId = useSelectedPropertyId();

  // Fetch KPIs from the dedicated endpoint
  const { data: kpis, isLoading: kpisLoading } = useDashboardKpis({
    propertyId,
    from: dateRange.from,
    to: dateRange.to,
  });

  // Fetch data
  const { data: arrivalsData, isLoading: arrivalsLoading } = useBookings({ status: BookingStatus.CONFIRMED, ...(propertyId ? { propertyId } : {}) });
  const { data: departuresData, isLoading: departuresLoading } = useBookings({ status: BookingStatus.CHECKED_IN, ...(propertyId ? { propertyId } : {}) });
  const { data: inHouseData, isLoading: inHouseLoading } = useBookings({ status: BookingStatus.CHECKED_IN, ...(propertyId ? { propertyId } : {}) });
  const { data: propertiesData, isLoading: propertiesLoading } = useProperties();
  const checkInMutation = useCheckInBooking();
  const checkOutMutation = useCheckOutBooking();

  // 7-day arrivals and departures queues
  const { data: arrivalsWeek, isLoading: arrivalsWeekLoading } = useArrivalsMultiDay(propertyId, 7);
  const { data: departuresWeek, isLoading: departuresWeekLoading } = useDeparturesMultiDay(propertyId, 7);

  // Normalise API responses (may return array or paginated { data: [], meta })
  const allArrivals: BookingData[] = useMemo(() => {
    const raw = arrivalsData;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  }, [arrivalsData]);

  const allCheckedIn: BookingData[] = useMemo(() => {
    const raw = departuresData;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  }, [departuresData]);

  const allInHouse: BookingData[] = useMemo(() => {
    const raw = inHouseData;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  }, [inHouseData]);

  const properties: Array<{ id: string; name: string; units: UnitData[] }> = useMemo(() => {
    const raw = propertiesData;
    const propList = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return propList.map((p: Record<string, unknown>) => ({
      id: p.id as string,
      name: p.name as string,
      units: ((p.units as UnitData[]) ?? []).map((u) => ({
        ...u,
        propertyId: p.id as string,
        propertyName: p.name as string,
        housekeepingStatus: (u.housekeepingStatus as HousekeepingStatus) ?? 'clean',
      })),
    }));
  }, [propertiesData]);

  const allUnits: UnitData[] = useMemo(
    () => properties.flatMap((p) => p.units),
    [properties],
  );

  // Filter arrivals/departures for today
  const todayArrivals = useMemo(
    () => allArrivals.filter((b) => isTodayDate(b.checkIn)),
    [allArrivals],
  );
  const todayDepartures = useMemo(
    () => allCheckedIn.filter((b) => isTodayDate(b.checkOut)),
    [allCheckedIn],
  );

  // Map unitId → current booking for the unit grid
  const bookingByUnitId = useMemo(() => {
    const map = new Map<string, BookingData>();
    for (const b of allCheckedIn) {
      map.set(b.unitId, b);
    }
    // Also include confirmed arrivals for today so they appear on arriving units
    for (const b of todayArrivals) {
      if (!map.has(b.unitId)) map.set(b.unitId, b);
    }
    return map;
  }, [allCheckedIn, todayArrivals]);

  const arrivingUnitIds = useMemo(() => new Set(todayArrivals.map((b) => b.unitId)), [todayArrivals]);
  const departingUnitIds = useMemo(() => new Set(todayDepartures.map((b) => b.unitId)), [todayDepartures]);

  // KPI counts
  const availableCount = useMemo(
    () => allUnits.filter((u) => !bookingByUnitId.has(u.id) || bookingByUnitId.get(u.id)?.status !== BookingStatus.CHECKED_IN).length,
    [allUnits, bookingByUnitId],
  );

  // Live occupancy — reconciled with the Room Status panel below. A unit counts
  // as occupied when it is flagged `occupied` in housekeeping OR has a guest
  // currently checked in. The backend KPI `occupancyRate` is a *period* metric
  // (occupied room-nights ÷ available room-nights over the KPI date window); on
  // a fresh window that has no bookings it reads 0.0% even while Room Status
  // shows occupied rooms, which reads as a contradiction. We surface the live
  // point-in-time occupancy instead so the two panels agree.
  const occupiedUnitsCount = useMemo(
    () =>
      allUnits.filter(
        (u) =>
          u.housekeepingStatus === 'occupied' ||
          bookingByUnitId.get(u.id)?.status === BookingStatus.CHECKED_IN,
      ).length,
    [allUnits, bookingByUnitId],
  );
  const liveOccupancyRate = allUnits.length > 0 ? (occupiedUnitsCount / allUnits.length) * 100 : 0;

  const isLoading = arrivalsLoading || departuresLoading || propertiesLoading;

  function handleUnitClick(unit: UnitData) {
    const booking = bookingByUnitId.get(unit.id) ?? null;
    setSelectedUnit(unit);
    setSelectedBooking(booking);
  }

  function handleCheckIn(bookingId: string) {
    checkInMutation.mutate({ id: bookingId }, {
      onSuccess: () => setSelectedBooking(null),
    });
  }

  function handleCheckOut(bookingId: string) {
    checkOutMutation.mutate({ id: bookingId }, {
      onSuccess: () => setSelectedBooking(null),
    });
  }

  if (isLoading && allUnits.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const kpiItems: KpiStripItem[] = [
    {
      icon: <LogIn size={16} className="text-green-600" />,
      label: 'Arrivals Today',
      value: todayArrivals.length,
      color: 'bg-green-50',
    },
    {
      icon: <LogOut size={16} className="text-orange-600" />,
      label: 'Departures Today',
      value: todayDepartures.length,
      color: 'bg-orange-50',
    },
    {
      icon: <BedDouble size={16} className="text-blue-600" />,
      label: 'In House',
      value: allInHouse.length,
      color: 'bg-blue-50',
    },
    {
      icon: <Home size={16} className="text-primary-600" />,
      label: 'Available Units',
      value: availableCount,
      color: 'bg-primary-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-0">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <DateRangeSelector
          from={dateRange.from}
          to={dateRange.to}
          onChange={(from, to) => setDateRange({ from, to })}
        />
      </div>

      {/* Getting Started Checklist */}
      <GettingStartedChecklist />

      {/* Revenue KPI Cards */}
      <section aria-label="Key performance indicators">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <RevenueKpiCard
            label="Revenue"
            value={kpis ? `${kpis.revenue.currency} ${kpis.revenue.total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : kpisLoading ? '...' : '--'}
            trend={kpis?.revenueTrend}
            icon={<DollarSign size={16} className="text-emerald-600" />}
            iconBg="bg-emerald-50"
          />
          <RevenueKpiCard
            label="Occupancy Rate"
            value={`${liveOccupancyRate.toFixed(1)}%`}
            trend={undefined}
            icon={<BarChart3 size={16} className="text-blue-600" />}
            iconBg="bg-blue-50"
          />
          <RevenueKpiCard
            label="ADR"
            value={kpis ? `${kpis.revenue.currency} ${kpis.adr.toFixed(2)}` : kpisLoading ? '...' : '--'}
            trend={kpis?.adrTrend}
            icon={<CalendarDays size={16} className="text-purple-600" />}
            iconBg="bg-purple-50"
          />
          <RevenueKpiCard
            label="RevPAR"
            value={kpis ? `${kpis.revenue.currency} ${kpis.revPar.toFixed(2)}` : kpisLoading ? '...' : '--'}
            trend={undefined}
            icon={<Hotel size={16} className="text-orange-600" />}
            iconBg="bg-orange-50"
          />
        </div>
      </section>

      {/* Today's Snapshot */}
      <section aria-label="Today's snapshot">
        <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Today</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SnapshotCard
            label="Arrivals Today"
            value={kpis?.arrivalsToday ?? todayArrivals.length}
            icon={<LogIn size={16} className="text-green-600" />}
            iconBg="bg-green-50"
          />
          <SnapshotCard
            label="Check-ins Today"
            value={kpis?.checkedInToday ?? 0}
            icon={<CheckCircle2 size={16} className="text-teal-600" />}
            iconBg="bg-teal-50"
          />
          <SnapshotCard
            label="Check-outs Today"
            value={kpis?.checkingOutToday ?? todayDepartures.length}
            icon={<LogOut size={16} className="text-orange-600" />}
            iconBg="bg-orange-50"
          />
          <SnapshotCard
            label="In-House"
            value={allInHouse.length}
            icon={<BedDouble size={16} className="text-blue-600" />}
            iconBg="bg-blue-50"
          />
        </div>
      </section>

      {/* Operations KPI Strip */}
      <KpiStrip items={kpiItems} />

      {/* Room Status Grid */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <BedDouble size={18} className="text-gray-400" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-900">Room Status</h2>
          <span className="ml-auto text-sm text-gray-500">{allUnits.length} units</span>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50">
          {(Object.entries(HK_CONFIG) as [HousekeepingStatus, typeof HK_CONFIG[HousekeepingStatus]][]).map(([key, cfg]) => (
            <span key={key} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className={cn('w-2.5 h-2.5 rounded-full', cfg.dot)} aria-hidden="true" />
              {cfg.label}
            </span>
          ))}
          <span className="flex items-center gap-1.5 text-xs text-gray-600 ml-2 pl-2 border-l border-gray-200">
            <span className="inline-block w-4 h-3 rounded-sm bg-green-100 border border-green-300" aria-hidden="true" />
            Arrival
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="inline-block w-4 h-3 rounded-sm bg-orange-100 border border-orange-300" aria-hidden="true" />
            Departure
          </span>
        </div>

        {propertiesLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-primary-600" />
          </div>
        ) : properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
              <BedDouble size={24} className="text-gray-400" aria-hidden="true" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">No properties yet</h3>
            <p className="text-sm text-gray-500 max-w-xs">Add a property from the Properties page to see units here.</p>
          </div>
        ) : (
          <div className="p-5 space-y-6">
            {properties.map((property) => (
              <div key={property.id}>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Home size={14} className="text-gray-400" aria-hidden="true" />
                  {property.name}
                  <span className="text-xs font-normal text-gray-400">({property.units.length} units)</span>
                </h3>
                {property.units.length === 0 ? (
                  <p className="text-sm text-gray-400 pl-5">No units configured.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {property.units.map((unit) => (
                      <UnitCard
                        key={unit.id}
                        unit={unit}
                        currentBooking={bookingByUnitId.get(unit.id)}
                        arrivingToday={arrivingUnitIds.has(unit.id)}
                        departingToday={departingUnitIds.has(unit.id)}
                        onClick={() => handleUnitClick(unit)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Arrivals (Next 7 Days) */}
      <SevenDayQueue
        title="Upcoming Arrivals (Next 7 Days)"
        icon={<LogIn size={18} />}
        dayGroups={arrivalsWeek}
        isLoading={arrivalsWeekLoading}
        emptyDayMessage="No arrivals scheduled."
        actionVariantColor="primary"
        renderEntry={(entry) => (
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 text-xs font-semibold shrink-0">
                {(entry.guest?.fullName ?? 'G').slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {entry.guest?.fullName ?? 'Unknown Guest'}
                </p>
                <p className="text-xs text-gray-500">
                  {entry.unit?.name ?? entry.unitId.slice(0, 8)}
                  {' '}&middot;{' '}
                  <span className={cn('inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded-full', SOURCE_BADGE_COLORS[entry.source] ?? 'bg-gray-100 text-gray-600')}>
                    {SOURCE_LABELS[entry.source] ?? entry.source}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button
                type="button"
                onClick={() => handleCheckIn(entry.id)}
                disabled={checkInMutation.isPending}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50"
              >
                {checkInMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />}
                Check In
              </button>
            </div>
          </div>
        )}
      />

      {/* Upcoming Departures (Next 7 Days) */}
      <SevenDayQueue
        title="Upcoming Departures (Next 7 Days)"
        icon={<LogOut size={18} />}
        dayGroups={departuresWeek}
        isLoading={departuresWeekLoading}
        emptyDayMessage="No departures scheduled."
        actionVariantColor="orange"
        renderEntry={(entry) => (
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold shrink-0">
                {(entry.guest?.fullName ?? 'G').slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {entry.guest?.fullName ?? 'Unknown Guest'}
                </p>
                <p className="text-xs text-gray-500">
                  {entry.unit?.name ?? entry.unitId.slice(0, 8)}
                  {' '}&middot;{' '}
                  {nightsCount(entry.checkIn, entry.checkOut)} nights
                  {' '}&middot;{' '}
                  {entry?.currency ?? 'USD'} {(parseFloat(entry?.totalAmount ?? '0') || 0).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <PrintBillButton
                bookingId={entry.id}
                variant="inline"
                label="Bill"
              />
              <button
                type="button"
                onClick={() => handleCheckOut(entry.id)}
                disabled={checkOutMutation.isPending}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-50"
              >
                {checkOutMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
                Check Out
              </button>
            </div>
          </div>
        )}
      />

      {/* In-House Guests */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <Users size={18} className="text-gray-400" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-900">In-House Guests</h2>
          <span className="ml-auto text-sm text-gray-500">{allInHouse.length}</span>
        </div>

        {inHouseLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="animate-spin text-primary-600" />
          </div>
        ) : allInHouse.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-gray-400">No guests currently in house.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100" role="list">
            {allInHouse.map((b) => {
              const unit = allUnits.find((u) => u.id === b.unitId);
              const checkOutDate = parseISO(b.checkOut);
              const msRemaining = checkOutDate.getTime() - Date.now();
              const daysRemaining = Math.ceil(msRemaining / 86400000);
              return (
                <li key={b.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold shrink-0">
                      {(b.guest?.fullName ?? b.guestName ?? 'G').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{b.guest?.fullName ?? b.guestName ?? '—'}</p>
                      <p className="text-xs text-gray-400">{unit?.name ?? b.unitId.slice(0, 8)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <p className="text-xs text-gray-600">Check-out {fmtDate(b.checkOut)}</p>
                    <p className="text-xs text-gray-400">
                      {daysRemaining > 0 ? `${daysRemaining} night${daysRemaining !== 1 ? 's' : ''} remaining` : 'Due today'}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Booking Detail Slide-over */}
      <BookingDetailSlideOver
        booking={selectedBooking}
        unit={selectedUnit}
        onClose={() => { setSelectedBooking(null); setSelectedUnit(null); }}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
        isCheckingIn={checkInMutation.isPending}
        isCheckingOut={checkOutMutation.isPending}
      />
    </div>
  );
}
